import { defineStore } from 'pinia';

// Генерация UUID
function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

// Получение или создание UUID сессии
function getSessionId() {
    let sessionId = sessionStorage.getItem('activitySessionId');
    if (!sessionId) {
        sessionId = generateUUID();
        sessionStorage.setItem('activitySessionId', sessionId);
    }
    return sessionId;
}

// Коды страниц для события onUserInteraction (бэкенд)
const PAGE_CODES = {
    styles: 'COLLECTION',
    personal: 'STYLECARD',
    other: 'IMAGES',
    compilation: 'CART',
    otherCompilation: 'CART'
};

// Дедупликация подряд идущих одинаковых чисел: [1,1,2,2,3,2] -> [1,2,3,2]
function dedupeConsecutive(arr) {
    if (!arr || arr.length === 0) return [];
    const result = [];
    for (const n of arr) {
        if (result[result.length - 1] !== n) result.push(n);
    }
    return result;
}

export const useActivityStore = defineStore('activity', {
    state: () => ({
        sessionId: getSessionId(),
        sessionStartTime: Date.now(),
        lastInteractionSendTime: 0, // время последней отправки onUserInteraction (для delta_seconds = интервал)
        pages: [],
        visitedPages: new Map(), // route -> timestamp для отслеживания возвратов
        currentPage: null,
        currentPageStartTime: null,
        // Отслеживание образов для разных страниц
        imagesTracking: {
            // 'styles': { viewed: Set(), maxDepth: 0, total: 0 }
        },
        // Отслеживание карты стиля
        personalCardTracking: {
            lastVisibleBlock: -1,
            blocks: []
        },
        // Последовательность прокруток по секциям для onUserInteraction (pageCode -> number[])
        scrollSequences: {
            COLLECTION: [],
            STYLECARD: [],
            IMAGES: [],
            CART: [],
            FORM: [],
            PREVIEW: []
        },
        // Секции, которые сейчас в зоне видимости (для определения "вошёл в зону" и повторного входа)
        sectionsInViewForSequence: {
            COLLECTION: new Set(),
            STYLECARD: new Set(),
            IMAGES: new Set(),
            CART: new Set(),
            FORM: new Set(),
            PREVIEW: new Set()
        },
        // Страницы, посещённые в текущем интервале (для отправки CART/PREVIEW с numbers: [])
        visitedPagesInInterval: new Set()
    }),

    actions: {
        // Инициализация страницы
        initPage(routeName, routePath) {
            const now = Date.now();
            
            // Защита от дублирования: если это та же страница с разницей < 1 секунды, игнорируем
            if (this.currentPage && this.currentPage.routeName === routeName) {
                const timeDiff = now - this.currentPage.timestamp;
                if (timeDiff < 1000) {
                    return;
                }
            }
            
            const isReturn = this.visitedPages.has(routeName);
            const previousVisit = isReturn ? this.visitedPages.get(routeName) : null;
            
            // Проверяем, что это действительно возврат (разница > 5 секунд)
            const realReturn = isReturn && previousVisit && (now - previousVisit > 5000);

            // Сохраняем текущую страницу
            if (this.currentPage) {
                this.finalizeCurrentPage();
            }

            this.currentPage = {
                route: routePath,
                routeName: routeName,
                timestamp: now,
                isReturn: realReturn, // Только реальные возвраты
                previousVisit: realReturn ? previousVisit : null,
                scrollDepth: 0,
                scrollRestored: false,
                _lastSentData: null, // Сбрасываем данные последней отправки для новой страницы
                _blocksSinceLastSend: [], // Блоки, просмотренные с момента последней отправки (только для personal)
                _imagesSinceLastSend: [] // Образы, просмотренные с момента последней отправки (для styles и other)
            };

            this.currentPageStartTime = now;

            // Анкета: добавляем номер шага в последовательность для onUserInteraction (page: FORM)
            const anketaStep = this.getAnketaStepNumber(routeName);
            if (anketaStep !== null) {
                this.sectionsInViewForSequence.FORM = new Set(); // считаем переход на шаг как "вход"
                this.appendToScrollSequence('FORM', anketaStep);
            }

            // CART и PREVIEW: фиксируем посещение страницы (даже без скролла отправим { page, numbers: [] })
            const pageCodeForRoute = this.getPageCodeForRoute(routeName);
            if (pageCodeForRoute) {
                this.visitedPagesInInterval.add(pageCodeForRoute);
            }

            // Если это возврат, восстанавливаем данные отслеживания
            if (realReturn) {
                this.restoreTrackingData(routeName);
            } else {
                this.visitedPages.set(routeName, now);
                
                // Сбрасываем tracking данные при первом визите
                if (routeName === 'personal') {
                    this.personalCardTracking = {
                        lastVisibleBlock: -1,
                        blocks: []
                    };
                } else if (routeName === 'styles' || routeName === 'other') {
                    // Сбрасываем tracking данных для образов при первом визите
                    this.imagesTracking[routeName] = {
                        viewed: new Set(),
                        maxDepth: -1,
                        total: 0 // Будет обновлено при инициализации
                    };
                    // Очищаем sessionStorage для этой страницы
                    sessionStorage.removeItem(`imagesTracking_${routeName}`);
                }
            }
        },

        // Финализация текущей страницы (перед переходом или отправкой)
        finalizeCurrentPage() {
            if (!this.currentPage) return;


            // Копируем временные данные в финальные
            if (this.currentPage._tempScroll) {
                this.currentPage.scroll = { ...this.currentPage._tempScroll };
            } else if (this.currentPage.routeName === 'styles') {
                const tracking = this.imagesTracking['styles'];
                if (tracking) {
                    // Используем образы из _imagesSinceLastSend (все, что были видимы в интервале)
                    let imagesFromInterval = this.currentPage._imagesSinceLastSend || [];
                    
                    // Если есть данные о последней отправке, добавляем промежуточные образы
                    // (для случая быстрого скролла, когда IntersectionObserver не успел сработать)
                    if (this.currentPage._lastSentData && this.currentPage._lastSentData.scroll) {
                        const lastMaxDepth = this.currentPage._lastSentData.scroll.maxDepth || -1;
                        const currentMaxDepth = tracking.maxDepth;
                        
                        // Если текущий maxDepth больше последнего, добавляем все промежуточные образы
                        if (currentMaxDepth > lastMaxDepth) {
                            for (let i = lastMaxDepth + 1; i <= currentMaxDepth; i++) {
                                if (!imagesFromInterval.includes(i)) {
                                    imagesFromInterval.push(i);
                                }
                            }
                        }
                    }
                    
                    const sortedImages = imagesFromInterval.sort((a, b) => a - b);
                    
                    this.currentPage.scroll = {
                        source: 'styles',
                        total: tracking.total,
                        viewedCount: sortedImages.length,
                        viewedStyles: sortedImages,
                        maxDepth: sortedImages.length > 0 ? Math.max(...sortedImages) : -1
                    };
                }
            } else if (this.currentPage.routeName === 'other') {
                const tracking = this.imagesTracking['other'];
                if (tracking) {
                    // Используем образы из _imagesSinceLastSend (все, что были видимы в интервале)
                    let imagesFromInterval = this.currentPage._imagesSinceLastSend || [];
                    
                    // Если есть данные о последней отправке, добавляем промежуточные образы
                    // (для случая быстрого скролла, когда IntersectionObserver не успел сработать)
                    if (this.currentPage._lastSentData && this.currentPage._lastSentData.scroll) {
                        const lastMaxDepth = this.currentPage._lastSentData.scroll.maxDepth || -1;
                        const currentMaxDepth = tracking.maxDepth;
                        
                        // Если текущий maxDepth больше последнего, добавляем все промежуточные образы
                        if (currentMaxDepth > lastMaxDepth) {
                            for (let i = lastMaxDepth + 1; i <= currentMaxDepth; i++) {
                                if (!imagesFromInterval.includes(i)) {
                                    imagesFromInterval.push(i);
                                }
                            }
                        }
                    }
                    
                    const sortedImages = imagesFromInterval.sort((a, b) => a - b);
                    
                    this.currentPage.scroll = {
                        source: 'other',
                        total: tracking.total,
                        viewedCount: sortedImages.length,
                        viewedStyles: sortedImages,
                        maxDepth: sortedImages.length > 0 ? Math.max(...sortedImages) : -1
                    };
                }
            }
            
            if (this.currentPage.routeName === 'personal') {
                // Получаем все блоки, которые были видимы в текущем интервале
                // (блоки из _blocksSinceLastSend + видимые сейчас блоки)
                const blocksFromInterval = new Set(this.currentPage._blocksSinceLastSend || []);
                
                // Добавляем видимые сейчас блоки (на случай, если они были видимы в начале интервала)
                const currentlyVisible = this.getCurrentlyVisiblePersonalBlocks();
                currentlyVisible.forEach(block => blocksFromInterval.add(block));
                
                const blocksToSend = Array.from(blocksFromInterval);
                
                this.currentPage.scroll = {
                    source: 'personal',
                    blocks: blocksToSend
                };
            }
            
            if (this.currentPage.routeName?.startsWith('anketa.')) {
                // Извлекаем номер шага из routeName (anketa.step1 -> 1)
                const stepMatch = this.currentPage.routeName.match(/step(\d+)/);
                if (stepMatch) {
                    this.currentPage.anketaStep = parseInt(stepMatch[1]);
                }
            }

            // Сохраняем копию данных для сравнения при следующей отправке
            const pageCopy = { ...this.currentPage };
            // Удаляем временные поля перед сохранением
            delete pageCopy._tempScroll;
            delete pageCopy._lastSentData;
            delete pageCopy.lastSentTimestamp;
            delete pageCopy._blocksSinceLastSend; // Не сохраняем в историю
            delete pageCopy._imagesSinceLastSend; // Не сохраняем в историю
            
            // Сохраняем данные для сравнения
            this.currentPage._lastSentData = JSON.parse(JSON.stringify(pageCopy));
            
            // Очищаем данные, просмотренные с момента последней отправки
            if (this.currentPage.routeName === 'personal') {
                this.currentPage._blocksSinceLastSend = [];
            } else if (this.currentPage.routeName === 'styles' || this.currentPage.routeName === 'other') {
                this.currentPage._imagesSinceLastSend = [];
            }
            
            this.pages.push(pageCopy);
        },

        // Инициализация отслеживания образов для страницы
        initImagesTracking(routeName, total) {
            if (!this.imagesTracking[routeName]) {
                // Если tracking данных нет, создаем новый Set
                // Восстановление из sessionStorage происходит только при возврате через restoreTrackingData
                this.imagesTracking[routeName] = {
                    viewed: new Set(),
                    maxDepth: -1,
                    total: total
                };
            } else {
                // Обновляем total если изменился
                if (this.imagesTracking[routeName].total !== total) {
                    this.imagesTracking[routeName].total = total;
                }
            }
        },

        // Отметить образ как просмотренный
        markImageViewed(routeName, index) {
            if (!this.imagesTracking[routeName]) {
                this.initImagesTracking(routeName, 0);
            }

            const tracking = this.imagesTracking[routeName];
            if (!tracking.viewed.has(index)) {
                tracking.viewed.add(index);
                tracking.maxDepth = Math.max(tracking.maxDepth, index);
                // Сохраняем в sessionStorage
                this.saveImagesTracking(routeName);
            }
            
            // Добавляем образ в список образов, просмотренных с момента последней отправки
            if (this.currentPage && (this.currentPage.routeName === 'styles' || this.currentPage.routeName === 'other')) {
                if (!this.currentPage._imagesSinceLastSend.includes(index)) {
                    this.currentPage._imagesSinceLastSend.push(index);
                }
            }
        },

        // Сохранение данных отслеживания образов
        saveImagesTracking(routeName) {
            const tracking = this.imagesTracking[routeName];
            if (tracking) {
                sessionStorage.setItem(`imagesTracking_${routeName}`, JSON.stringify({
                    viewed: Array.from(tracking.viewed),
                    maxDepth: tracking.maxDepth,
                    total: tracking.total
                }));
            }
        },

        // Восстановление данных отслеживания при возврате
        restoreTrackingData(routeName) {
            
            // Для образов
            if (routeName === 'styles' || routeName === 'other') {
                const stored = sessionStorage.getItem(`imagesTracking_${routeName}`);
                if (stored) {
                    try {
                        const parsed = JSON.parse(stored);
                        this.imagesTracking[routeName] = {
                            viewed: new Set(parsed.viewed || []),
                            maxDepth: parsed.maxDepth || -1,
                            total: parsed.total || 0
                        };
                    } catch (e) {}
                } else {
                }
            }
            // Для карты стиля
            if (routeName === 'personal') {
                const stored = sessionStorage.getItem('personalCardTracking');
                if (stored) {
                    try {
                        const parsed = JSON.parse(stored);
                        this.personalCardTracking = {
                            lastVisibleBlock: parsed.lastVisibleBlock || -1,
                            blocks: parsed.blocks || []
                        };
                    } catch (e) {}
                }
            }
        },

        // Обновление глубины скролла страницы
        updateScrollDepth(depth) {
            if (this.currentPage) {
                this.currentPage.scrollDepth = Math.max(this.currentPage.scrollDepth || 0, depth);
            }
        },
        
        // Глобальное обновление скролла (для всех страниц)
        updateGlobalScrollDepth() {
            if (!this.currentPage) return;
            
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
            const scrollDepth = scrollHeight > 0 ? Math.round((scrollTop / scrollHeight) * 100) : 0;
            
            this.updateScrollDepth(scrollDepth);
        },

        // Отметить что скролл был восстановлен
        markScrollRestored() {
            if (this.currentPage) {
                this.currentPage.scrollRestored = true;
            }
        },

        // Обновление отслеживания карты стиля
        updatePersonalCardBlock(blockIndex, blockName) {
            // Обновляем lastVisibleBlock если это новый блок (больше текущего индекса) или если это первый блок
            if (blockIndex > this.personalCardTracking.lastVisibleBlock || (blockIndex >= 0 && this.personalCardTracking.lastVisibleBlock === -1)) {
                this.personalCardTracking.lastVisibleBlock = blockIndex;
                
                if (!this.personalCardTracking.blocks.includes(blockName)) {
                    this.personalCardTracking.blocks.push(blockName);
                }
                
                // Сохраняем в sessionStorage
                sessionStorage.setItem('personalCardTracking', JSON.stringify({
                    lastVisibleBlock: this.personalCardTracking.lastVisibleBlock,
                    blocks: this.personalCardTracking.blocks
                }));
            }
            
            // ВСЕГДА добавляем блок в список блоков, просмотренных с момента последней отправки
            // (независимо от индекса, если блок был виден в текущем интервале)
            if (this.currentPage && this.currentPage.routeName === 'personal') {
                if (!this.currentPage._blocksSinceLastSend.includes(blockName)) {
                    this.currentPage._blocksSinceLastSend.push(blockName);
                }
            }
        },

        // --- onUserInteraction: последовательность прокруток по секциям ---
        // Добавить номер секции в последовательность при входе в зону видимости
        appendToScrollSequence(pageCode, sectionNumber) {
            if (!this.scrollSequences[pageCode]) return;
            const inView = this.sectionsInViewForSequence[pageCode];
            if (inView.has(sectionNumber)) return; // уже в зоне — не дублируем
            inView.add(sectionNumber);
            const seq = this.scrollSequences[pageCode];
            // не дублируем подряд одно и то же число
            if (seq[seq.length - 1] !== sectionNumber) {
                seq.push(sectionNumber);
            }
        },

        // Убрать секцию из «в зоне видимости» при выходе (чтобы повторный вход снова попал в sequence)
        removeFromScrollSequenceView(pageCode, sectionNumber) {
            if (this.sectionsInViewForSequence[pageCode]) {
                this.sectionsInViewForSequence[pageCode].delete(sectionNumber);
            }
        },

        // Номер шага анкеты из имени маршрута (для page: FORM, numbers: [1,2,3,...])
        getAnketaStepNumber(routeName) {
            if (!routeName || typeof routeName !== 'string') return null;
            if (routeName === 'anketa.fillout') return 1;
            const stepMatch = routeName.match(/^anketa\.step(\d+)$/);
            if (stepMatch) return parseInt(stepMatch[1], 10);
            if (routeName === 'anketa.completion') return 10;
            return null;
        },

        // Код страницы для маршрута — чтобы отправлять { page, numbers: [] } даже без скролла
        getPageCodeForRoute(routeName) {
            if (!routeName || typeof routeName !== 'string') return null;
            if (routeName === 'home' || routeName === 'init' || routeName === 'policy') return 'PREVIEW';
            if (routeName === 'compilation' || routeName === 'otherCompilation') return 'CART';
            if (routeName === 'personal') return 'STYLECARD';
            if (routeName === 'styles') return 'COLLECTION';
            if (routeName === 'other') return 'IMAGES';
            if (routeName.startsWith('anketa.')) return 'FORM';
            return null;
        },

        // Получить payload для события onUserInteraction
        // currentRouteName — имя маршрута в момент отправки (из $route.name), чтобы при бездействии всё равно отправлять PREVIEW/CART
        getInteractionData(currentRouteName) {
            const now = Date.now();
            // delta_seconds — интервал с последней отправки (обычно 10 сек), не общая длительность сессии
            const delta_seconds = this.lastInteractionSendTime
                ? Math.round((now - this.lastInteractionSendTime) / 1000)
                : 10;
            this.lastInteractionSendTime = now;

            // Текущая страница в момент отправки: главная и корзина всегда попадают в interactions (с numbers: [] при бездействии)
            const codeFromRoute = currentRouteName != null ? this.getPageCodeForRoute(currentRouteName) : null;
            if (codeFromRoute) {
                this.visitedPagesInInterval.add(codeFromRoute);
            }
            if (codeFromRoute === 'FORM' && currentRouteName) {
                const anketaStep = this.getAnketaStepNumber(currentRouteName);
                if (anketaStep !== null) {
                    this.appendToScrollSequence('FORM', anketaStep);
                }
            }
            // На случай если currentRouteName не передан — используем currentPage из store
            if (!codeFromRoute && this.currentPage) {
                const code = this.getPageCodeForRoute(this.currentPage.routeName);
                if (code) this.visitedPagesInInterval.add(code);
            }

            const interactions = [];
            const pageCodes = ['COLLECTION', 'STYLECARD', 'IMAGES', 'CART', 'FORM', 'PREVIEW'];
            for (const page of pageCodes) {
                const numbers = dedupeConsecutive(this.scrollSequences[page] || []) || [];
                const wasVisited = this.visitedPagesInInterval.has(page);
                if (numbers.length > 0 || wasVisited) {
                    interactions.push({ page, numbers });
                }
            }
            const data = { delta_seconds, interactions };
            // После отправки очищаем последовательности и посещения для следующего интервала
            for (const page of pageCodes) {
                this.scrollSequences[page] = [];
                this.sectionsInViewForSequence[page] = new Set();
            }
            this.visitedPagesInInterval = new Set();
            return data;
        },

        // Получение данных для отправки
        getActivityData(clientHash) {
            const data = {
                clientHash: clientHash,
                sessionId: this.sessionId,
                timestamp: Date.now(),
                pages: []
            };

            // Всегда отправляем событие для подсчета длительности сессии на бэкенде
            if (this.currentPage) {
                // Обновляем данные отслеживания перед отправкой
                this.updateCurrentPageData();
                
                // Проверяем, есть ли изменения (скролл, новые образы)
                const hasChanges = this.hasPageChanges();
                
                if (hasChanges) {
                    // Есть изменения - финализируем и добавляем в стек
                    this.finalizeCurrentPage();
                    data.pages = [...this.pages];
                    this.pages = []; // Очищаем стек
                    
                    // Обновляем timestamp последней отправки для текущей страницы
                    this.currentPage.lastSentTimestamp = Date.now();
                    
                    // Очищаем блоки, просмотренные с момента последней отправки (уже очищены в finalizeCurrentPage)
                } else {
                    // Нет изменений - отправляем текущую страницу с актуальными данными
                    // (только видимые сейчас образы, текущая позиция скролла)
                    const currentPageSnapshot = this.getCurrentPageSnapshot();
                    data.pages = [...this.pages, currentPageSnapshot];
                    this.pages = []; // Очищаем стек
                    
                }
            } else {
                // Нет текущей страницы - просто отправляем существующий стек
                data.pages = [...this.pages];
                this.pages = [];
            }
            
            // Очищаем visitedPages для отправленных страниц, чтобы в новом стеке они не считались возвратами
            // Оставляем только текущую страницу, если она есть
            if (this.currentPage) {
                const currentRoute = this.currentPage.routeName;
                const currentTimestamp = this.currentPage.timestamp;
                this.visitedPages.clear();
                this.visitedPages.set(currentRoute, currentTimestamp);
            } else {
                this.visitedPages.clear();
            }

            return data;
        },
        
        // Получение snapshot текущей страницы с только видимыми сейчас образами
        getCurrentPageSnapshot() {
            if (!this.currentPage) return null;
            
            const snapshot = {
                route: this.currentPage.route,
                routeName: this.currentPage.routeName,
                timestamp: this.currentPage.timestamp,
                isReturn: this.currentPage.isReturn,
                previousVisit: this.currentPage.previousVisit,
                scrollDepth: this.currentPage.scrollDepth || 0,
                scrollRestored: this.currentPage.scrollRestored || false
            };
            
            // Для страниц с образами - получаем образы из интервала или видимые сейчас
            if (this.currentPage.routeName === 'styles') {
                const tracking = this.imagesTracking['styles'];
                if (tracking) {
                    // Используем образы из _imagesSinceLastSend (все, что были видимы в интервале)
                    let imagesFromInterval = this.currentPage._imagesSinceLastSend || [];
                    
                    // Если есть данные о последней отправке, добавляем промежуточные образы
                    if (this.currentPage._lastSentData && this.currentPage._lastSentData.scroll) {
                        const lastMaxDepth = this.currentPage._lastSentData.scroll.maxDepth || -1;
                        const currentMaxDepth = tracking.maxDepth;
                        
                        // Если текущий maxDepth больше последнего, добавляем все промежуточные образы
                        if (currentMaxDepth > lastMaxDepth) {
                            for (let i = lastMaxDepth + 1; i <= currentMaxDepth; i++) {
                                if (!imagesFromInterval.includes(i)) {
                                    imagesFromInterval.push(i);
                                }
                            }
                        }
                    }
                    
                    // Если нет образов в интервале, но есть видимые сейчас - используем их
                    if (imagesFromInterval.length === 0) {
                        imagesFromInterval = this.getCurrentlyVisibleImageIndices('styles');
                    }
                    
                    const sortedImages = imagesFromInterval.sort((a, b) => a - b);
                    
                    if (sortedImages.length > 0) {
                        snapshot.scroll = {
                            source: 'styles',
                            total: tracking.total,
                            viewedCount: sortedImages.length,
                            viewedStyles: sortedImages,
                            maxDepth: Math.max(...sortedImages)
                        };
                    } else {
                        snapshot.scroll = {
                            source: 'styles',
                            total: tracking.total,
                            viewedCount: 0,
                            viewedStyles: [],
                            maxDepth: -1
                        };
                    }
                }
            } else if (this.currentPage.routeName === 'other') {
                const tracking = this.imagesTracking['other'];
                if (tracking) {
                    // Используем образы из _imagesSinceLastSend (все, что были видимы в интервале)
                    let imagesFromInterval = this.currentPage._imagesSinceLastSend || [];
                    
                    // Если есть данные о последней отправке, добавляем промежуточные образы
                    if (this.currentPage._lastSentData && this.currentPage._lastSentData.scroll) {
                        const lastMaxDepth = this.currentPage._lastSentData.scroll.maxDepth || -1;
                        const currentMaxDepth = tracking.maxDepth;
                        
                        // Если текущий maxDepth больше последнего, добавляем все промежуточные образы
                        if (currentMaxDepth > lastMaxDepth) {
                            for (let i = lastMaxDepth + 1; i <= currentMaxDepth; i++) {
                                if (!imagesFromInterval.includes(i)) {
                                    imagesFromInterval.push(i);
                                }
                            }
                        }
                    }
                    
                    // Если нет образов в интервале, но есть видимые сейчас - используем их
                    if (imagesFromInterval.length === 0) {
                        imagesFromInterval = this.getCurrentlyVisibleImageIndices('other');
                    }
                    
                    const sortedImages = imagesFromInterval.sort((a, b) => a - b);
                    
                    if (sortedImages.length > 0) {
                        snapshot.scroll = {
                            source: 'other',
                            total: tracking.total,
                            viewedCount: sortedImages.length,
                            viewedStyles: sortedImages,
                            maxDepth: Math.max(...sortedImages)
                        };
                    } else {
                        snapshot.scroll = {
                            source: 'other',
                            total: tracking.total,
                            viewedCount: 0,
                            viewedStyles: [],
                            maxDepth: -1
                        };
                    }
                }
            } else if (this.currentPage.routeName === 'personal') {
                // Получаем только видимые сейчас блоки
                const visibleBlocks = this.getCurrentlyVisiblePersonalBlocks();
                snapshot.scroll = {
                    source: 'personal',
                    blocks: visibleBlocks
                };
            } else if (this.currentPage.routeName?.startsWith('anketa.')) {
                const stepMatch = this.currentPage.routeName.match(/step(\d+)/);
                if (stepMatch) {
                    snapshot.anketaStep = parseInt(stepMatch[1]);
                }
            }
            
            return snapshot;
        },
        
        // Получение индексов образов, которые видимы прямо сейчас
        getCurrentlyVisibleImageIndices(routeName) {
            const visibleIndices = [];
            const styleElements = document.querySelectorAll('.full');
            const tracking = this.imagesTracking[routeName];
            
            if (!tracking) return [];
            
            // Получаем массив всех образов для определения индекса
            // Используем порядок элементов в DOM для определения индекса
            styleElements.forEach((el, domIndex) => {
                // Проверяем, виден ли элемент в viewport
                const rect = el.getBoundingClientRect();
                const windowHeight = window.innerHeight || document.documentElement.clientHeight;
                const windowWidth = window.innerWidth || document.documentElement.clientWidth;
                
                // Элемент виден, если хотя бы часть его находится в viewport
                const isVisible = (
                    rect.top < windowHeight &&
                    rect.bottom > 0 &&
                    rect.left < windowWidth &&
                    rect.right > 0
                );
                
                if (isVisible) {
                    // Используем порядковый номер в DOM как индекс образа
                    // (элементы рендерятся в том же порядке, что и в массиве data.images)
                    if (domIndex < tracking.total) {
                        visibleIndices.push(domIndex);
                    }
                }
            });
            
            return visibleIndices;
        },
        
        // Получение блоков карты стиля, которые видимы прямо сейчас
        getCurrentlyVisiblePersonalBlocks() {
            const BLOCK_ORDER = [
                'greetings',
                'look',
                'colors',
                'combinations',
                'prints',
                'figure',
                'clothes',
                'thats-all'
            ];
            
            const visibleBlocks = [];
            
            BLOCK_ORDER.forEach((blockName) => {
                let selector = `.${blockName}`;
                if (blockName === 'greetings') {
                    selector = `.greetings, .greetings-collapsed`;
                }
                
                const element = document.querySelector(selector);
                if (!element) {
                    return;
                }
                
                // Проверяем, виден ли элемент в viewport
                const rect = element.getBoundingClientRect();
                const windowHeight = window.innerHeight || document.documentElement.clientHeight;
                const windowWidth = window.innerWidth || document.documentElement.clientWidth;
                
                // Элемент виден, если хотя бы часть его находится в viewport
                const isVisible = (
                    rect.top < windowHeight &&
                    rect.bottom > 0 &&
                    rect.left < windowWidth &&
                    rect.right > 0
                );
                
                if (isVisible) {
                    visibleBlocks.push(blockName);
                }
            });
            
            return visibleBlocks;
        },
        
        // Обновление данных текущей страницы перед проверкой изменений
        updateCurrentPageData() {
            if (!this.currentPage) return;
            
            // Обновляем scrollDepth
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
            const scrollDepth = scrollHeight > 0 ? Math.round((scrollTop / scrollHeight) * 100) : 0;
            this.currentPage.scrollDepth = Math.max(this.currentPage.scrollDepth || 0, scrollDepth);
            
            // Обновляем данные отслеживания образов (без финализации)
            if (this.currentPage.routeName === 'styles') {
                const tracking = this.imagesTracking['styles'];
                if (tracking) {
                    // Используем образы из _imagesSinceLastSend
                    let imagesFromInterval = this.currentPage._imagesSinceLastSend || [];
                    
                    // Если есть данные о последней отправке, добавляем промежуточные образы
                    if (this.currentPage._lastSentData && this.currentPage._lastSentData.scroll) {
                        const lastMaxDepth = this.currentPage._lastSentData.scroll.maxDepth || -1;
                        const currentMaxDepth = tracking.maxDepth;
                        
                        // Если текущий maxDepth больше последнего, добавляем все промежуточные образы
                        if (currentMaxDepth > lastMaxDepth) {
                            for (let i = lastMaxDepth + 1; i <= currentMaxDepth; i++) {
                                if (!imagesFromInterval.includes(i)) {
                                    imagesFromInterval.push(i);
                                }
                            }
                        }
                    }
                    
                    const sortedImages = imagesFromInterval.sort((a, b) => a - b);
                    
                    this.currentPage._tempScroll = {
                        source: 'styles',
                        total: tracking.total,
                        viewedCount: sortedImages.length,
                        viewedStyles: sortedImages,
                        maxDepth: sortedImages.length > 0 ? Math.max(...sortedImages) : -1
                    };
                }
            } else if (this.currentPage.routeName === 'other') {
                const tracking = this.imagesTracking['other'];
                if (tracking) {
                    // Используем образы из _imagesSinceLastSend
                    let imagesFromInterval = this.currentPage._imagesSinceLastSend || [];
                    
                    // Если есть данные о последней отправке, добавляем промежуточные образы
                    if (this.currentPage._lastSentData && this.currentPage._lastSentData.scroll) {
                        const lastMaxDepth = this.currentPage._lastSentData.scroll.maxDepth || -1;
                        const currentMaxDepth = tracking.maxDepth;
                        
                        // Если текущий maxDepth больше последнего, добавляем все промежуточные образы
                        if (currentMaxDepth > lastMaxDepth) {
                            for (let i = lastMaxDepth + 1; i <= currentMaxDepth; i++) {
                                if (!imagesFromInterval.includes(i)) {
                                    imagesFromInterval.push(i);
                                }
                            }
                        }
                    }
                    
                    const sortedImages = imagesFromInterval.sort((a, b) => a - b);
                    
                    this.currentPage._tempScroll = {
                        source: 'other',
                        total: tracking.total,
                        viewedCount: sortedImages.length,
                        viewedStyles: sortedImages,
                        maxDepth: sortedImages.length > 0 ? Math.max(...sortedImages) : -1
                    };
                }
            } else if (this.currentPage.routeName === 'personal') {
                // Используем блоки, просмотренные с момента последней отправки
                this.currentPage._tempScroll = {
                    source: 'personal',
                    blocks: [...(this.currentPage._blocksSinceLastSend || [])]
                };
            }
        },
        
        // Проверка, есть ли изменения в текущей странице
        hasPageChanges() {
            if (!this.currentPage) return false;
            
            // Если это первая проверка (нет сохраненных данных)
            if (!this.currentPage._lastSentData) {
                return true; // Всегда отправляем первый раз
            }
            
            const last = this.currentPage._lastSentData;
            const current = this.currentPage;
            
            // Проверяем изменения в scrollDepth (если изменился более чем на 5%)
            const scrollDiff = Math.abs((current.scrollDepth || 0) - (last.scrollDepth || 0));
            if (scrollDiff > 5) {
                return true;
            }
            
            // Проверяем изменения в отслеживании образов
            if (current.routeName === 'styles' || current.routeName === 'other') {
                // Проверяем, есть ли новые образы с момента последней отправки
                const newImages = current._imagesSinceLastSend || [];
                if (newImages.length > 0) {
                    return true; // Есть новые образы
                }
                // Также проверяем изменения в scrollDepth (уже проверено выше)
            } else if (current.routeName === 'personal') {
                // Проверяем, есть ли новые блоки с момента последней отправки
                const newBlocks = current._blocksSinceLastSend || [];
                if (newBlocks.length > 0) {
                    return true; // Есть новые блоки
                }
                // Также проверяем изменения в scrollDepth (уже проверено выше)
            }
            
            return false; // Нет изменений
        },

        // Очистка данных (при необходимости)
        clear() {
            this.pages = [];
            this.currentPage = null;
            this.currentPageStartTime = null;
        }
    }
});
